import React, { useEffect, useMemo, useRef } from "react";
import { useApps } from "./AppProvider";
import { useAccess } from "providers/access";
import { useUpdate } from "@appsinti/trigger-update";
import { fetchAndStoreStatsPreview } from "@appsinti/stats-frontend/preview/fetch";
import { fetchAndStoreStatsSummaryPreview } from "@appsinti/stats-summary-frontend/preview/fetch";

let runCount = 0;

const PreviewSync: React.FC = () => {
  const { apps, summaryApp } = useApps();
  const { groups } = useAccess();
  const { update, addLoadingState, resolveLoadingState } = useUpdate();

  const statsAppIds = useMemo(() => {
    const ids: number[] = [];
    if (apps) {
      for (const groupId in apps) {
        for (const app of apps[groupId]) {
          if (app.module === "stats") ids.push(app.id);
        }
      }
    }
    return ids;
  }, [apps]);

  const summaryAppId = summaryApp ? summaryApp.id : null;

  const loadingRef = useRef({ addLoadingState, resolveLoadingState });
  loadingRef.current = { addLoadingState, resolveLoadingState };

  const runSync = (fetches: (signal: AbortSignal) => Promise<unknown>[]) => {
    const controller = new AbortController();
    // Scoped per run so an aborted run cannot resolve the loading state that the
    // run replacing it just added.
    const loadingKey = `preview-sync-${++runCount}`;

    const refresh = async () => {
      loadingRef.current.addLoadingState(loadingKey);
      try {
        await Promise.allSettled(fetches(controller.signal));
      } finally {
        loadingRef.current.resolveLoadingState(loadingKey);
      }
    };

    refresh();

    return () => controller.abort();
  };

  useEffect(() => {
    if (statsAppIds.length === 0) return;

    return runSync((signal) =>
      statsAppIds.map((id) => fetchAndStoreStatsPreview(id, signal)),
    );
  }, [update, statsAppIds]);

  useEffect(() => {
    if (!summaryAppId) return;

    return runSync((signal) => [
      fetchAndStoreStatsSummaryPreview(summaryAppId, groups, signal),
    ]);
  }, [update, summaryAppId, groups]);

  return null;
};

export default PreviewSync;
